import { memo, useCallback, useMemo, useRef, useState } from 'react'
import Button from '../button'
import { compareMultipleFileExtension } from 'utils/helpers'
import toast from 'react-hot-toast'
import { CloseIcon, UploadIcon } from 'components/icons'
import { uploadAggregatorImg } from 'utils/api/aggregatorApi'

type ImgUploadButtonType = {
  supportedFileTypes?: string[]
  aggregatorID: string
  confirmButtonText: string
  defaultImg: string
}

const ImgUploadButton = ({
  confirmButtonText = 'Confirm',
  supportedFileTypes = ['.png', '.jpeg', '.jpg', '.webp'],
  aggregatorID,
  defaultImg,
}: ImgUploadButtonType) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(defaultImg ?? null)
  const [loading, setLoading] = useState(false)
  // To handle input change
  const handleFileChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    // Will check if file exists
    if (target.files && target.files.length > 0) {
      // Will check if the extension of file matches the required extension

      if (compareMultipleFileExtension(target?.files[0], supportedFileTypes)) {
        const file = target.files?.[0]
        setSelectedFile(target.files[0])
        const reader = new FileReader()
        reader.onloadend = () => {
          setImageUrl(reader.result as string)
        }
        reader.readAsDataURL(file)
      } else {
        toast.error('Invalid File Format', {
          duration: 3000,
        })
      }
    }
  }

  //  To handle reset
  const resetFileUpload = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation()

    if (inputRef.current) {
      inputRef.current.value = ''
      setSelectedFile(null)
      setImageUrl(null)
    }
  }, [])

  // Will be triggered on div/button click to explicitly trigger file input tag
  const handleSelectFile = () => {
    inputRef.current?.click()
  }

  const handleFileUpload = async () => {
    const toastId = toast.loading('Uploading file', { duration: Infinity })
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('image', selectedFile!)

      const { data: response } = await uploadAggregatorImg(
        aggregatorID,
        formData,
      )

      // Update the image URL with the backend response
      if (response.success && response.imageUrl) {
        toast.success('File Uploaded Successfully', {
          duration: 5000,
          id: toastId,
        })
      }

      // Reset selected file but keep the uploaded image URL
      setSelectedFile(null)
    } catch (error) {
      toast.error('File Upload Failed', {
        id: toastId,
      })
      console.error(error) // Log for debugging
    } finally {
      setLoading(false)
    }
  }

  // Will render the buttons Based on the progress
  const renderActionButton = useMemo(() => {
    return (
      <Button
        buttonText={confirmButtonText}
        onClick={handleFileUpload}
        disabled={!selectedFile || loading}
        loading={loading}
        className="mt-2 w-full"
        type="button"
        icon={
          <svg
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
          >
            <path d="M16 16l-4-4-4 4" />
            <path d="M12 12v9" />
            <path d="M20.39 18.39A5.5 5.5 0 0 0 18 9h-1.26a8 8 0 1 0-13.68 7.5" />
          </svg>
        }
      />
    )
  }, [selectedFile, imageUrl])

  return (
    <div className="mt-3">
      <input
        className="hidden"
        type="file"
        onChange={handleFileChange}
        accept={supportedFileTypes.join(',')}
        ref={inputRef}
      />

      {/* Clickable image preview or placeholder */}
      <div
        className=" flex h-44 w-44 cursor-pointer items-center justify-center border border-dashed border-slate-500 transition-colors hover:border-slate-600"
        onClick={handleSelectFile}
      >
        {imageUrl ? (
          <div className="relative  h-full w-full ">
            <img
              src={imageUrl}
              alt="Aggregator"
              className="h-full w-full object-contain p-1"
            />
            <Button
              onClick={resetFileUpload}
              type="button"
              variant="custom"
              icon2={<CloseIcon />}
              icon2ClassName="!h-3.5 !w-3.5 text-[#161C28] "
              className="absolute  right-0 top-0  bg-gray-50 p-1"
            />
          </div>
        ) : (
          <div className="flex h-full  w-full items-center justify-center bg-slate-200 p-2">
            <div className="flex flex-col items-center gap-3  text-center text-slate-600">
              <div className="size-8">
                <UploadIcon />
              </div>
              <span className="text-sm">Click to upload image</span>
            </div>
          </div>
        )}
      </div>

      {renderActionButton}
    </div>
  )
}

export default memo(ImgUploadButton)
